import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import './CreateCircle.css';
import './CircleClients.css';

const CircleClients = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [circle, setCircle] = useState(null);
  const [sharedClients, setSharedClients] = useState([]);
  const [myClients, setMyClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSharePicker, setShowSharePicker] = useState(false);
  const [sharing, setSharing] = useState(null);
  const [unsharing, setUnsharing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMsg, setSuccessMsg] = useState(null);
  const [showAllClients, setShowAllClients] = useState(false);
  const CLIENTS_PREVIEW = 8;
  const [personas, setPersonas] = useState({});
  const [copiedToken, setCopiedToken] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };

  const fetchData = useCallback(async () => {
    try {
      // Fetch circle info + shared clients in parallel
      const [circleRes, clientsRes, myClientsRes, personasRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/circles/mine`, { headers: authHeaders }),
        fetch(`${process.env.REACT_APP_API_URL}/api/circles/${id}/clients`, { headers: authHeaders }),
        fetch(`${process.env.REACT_APP_API_URL}/api/clients`, { headers: authHeaders }),
        fetch(`${process.env.REACT_APP_API_URL}/api/circles/${id}/personas`, { headers: authHeaders }),
      ]);

      if (circleRes.ok) {
        const data = await circleRes.json();
        setCircle(data.circle);
      }

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setSharedClients(data.clients || []);
      }

      if (myClientsRes.ok) {
        const data = await myClientsRes.json();
        const clients = Array.isArray(data) ? data : data.clients || [];
        setMyClients(clients);
      }

      if (personasRes.ok) {
        const data = await personasRes.json();
        setPersonas(data.personas || {});
      }
    } catch (err) {
      console.error('[CircleClients] fetch:', err);
      setError('Failed to load clients.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleShare = async (clientId) => {
    setSharing(clientId);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/circles/${id}/clients/${clientId}/share`,
        { method: 'POST', headers: authHeaders }
      );
      const data = await res.json();
      if (res.ok) {
        setSuccessMsg(data.message);
        setTimeout(() => setSuccessMsg(null), 3000);
        setShowSharePicker(false);
        fetchData();
      } else {
        setError(data.error || 'Failed to share client.');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      console.error('[CircleClients] share:', err);
    } finally {
      setSharing(null);
    }
  };

  const handleUnshare = async (clientId, clientName) => {
    if (!window.confirm(`Remove ${clientName} from the Circle? They'll only be visible to you again.`)) return;
    setUnsharing(clientId);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/circles/${id}/clients/${clientId}/share`,
        { method: 'DELETE', headers: authHeaders }
      );
      if (res.ok) {
        setSuccessMsg(`${clientName} removed from Circle.`);
        setTimeout(() => setSuccessMsg(null), 3000);
        fetchData();
      }
    } catch (err) {
      console.error('[CircleClients] unshare:', err);
    } finally {
      setUnsharing(null);
    }
  };

  const getInitials = (firstName, lastName) =>
    `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

  const getAvatarColor = (index) => {
    const colors = ['#1B2B4B', '#E8654A', '#10B981', '#3B82F6', '#8B5CF6'];
    return colors[index % colors.length];
  };

  // Clients not yet shared to this circle
  const sharedIds = new Set(sharedClients.map(c => c._id));
  const unsharedClients = myClients.filter(c => !sharedIds.has(c._id) && !c.circleId);
  const filteredUnshared = unsharedClients.filter(c =>
    `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const userId = localStorage.getItem('userId');

  if (loading) {
    return (
      <div className="circle-page">
        <div className="circle-container">
          <div className="circle-loading">Loading shared clients...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="circle-page">
      <div className="circle-container" style={{ maxWidth: '680px' }}>

        {/* Nav */}
        <div className="cc-nav">
          <button className="back-to-dashboard-btn" onClick={() => navigate(`/circles/${id}/manage`)}>
            ← Circle
          </button>
          <button className="back-to-dashboard-btn" onClick={() => navigate('/dashboard')}>
            Dashboard
          </button>
        </div>

        {/* Header */}
        <div className="circle-header">
          <div className="circle-sparkle">✦</div>
          <h1>Shared clients</h1>
          {circle && (
            <span className="circle-plan-badge">{circle.name}</span>
          )}
        </div>

        {/* Messages */}
        {successMsg && <div className="cc-success">✓ {successMsg}</div>}
        {error && <div className="circle-error">⚠️ {error}</div>}

        {/* Share button */}
        <div style={{ marginBottom: '1.5rem' }}>
          <button
            className="cc-share-trigger"
            onClick={() => setShowSharePicker(!showSharePicker)}
          >
            {showSharePicker ? '✕ Cancel' : '+ Share a client with the Circle'}
          </button>
        </div>

        {/* Client picker */}
        {showSharePicker && (
          <div className="manage-section" style={{ marginBottom: '1.5rem' }}>
            <h2 className="section-label">Your clients — select one to share</h2>
            <input
              type="text"
              className="circle-name-input"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginBottom: '1rem' }}
            />
            {filteredUnshared.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', textAlign: 'center', padding: '1rem' }}>
                {unsharedClients.length === 0
                  ? 'All your clients are already shared, or you have no clients yet.'
                  : 'No clients match your search.'}
              </p>
            ) : (
              <div className="cc-picker-list">
                {filteredUnshared.map((client) => (
                  <div key={client._id} className="cc-picker-row">
                    <div className="cc-avatar" style={{ background: '#1B2B4B' }}>
                      {getInitials(client.firstName, client.lastName)}
                    </div>
                    <div className="cc-client-info">
                      <p className="cc-client-name">{client.firstName} {client.lastName}</p>
                      <p className="cc-client-meta">{client.email}</p>
                    </div>
                    <button
                      className="cc-share-btn"
                      onClick={() => handleShare(client._id)}
                      disabled={sharing === client._id}
                    >
                      {sharing === client._id ? 'Sharing...' : 'Share'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Shared clients list */}
        {sharedClients.length === 0 ? (
          <div className="cc-empty">
            <div className="cc-empty-icon">👥</div>
            <h3>No shared clients yet</h3>
            <p>Share a client with your Circle so teammates can see their sessions, notes, and AI summaries.</p>
            <button
              className="create-circle-btn"
              onClick={() => setShowSharePicker(true)}
              style={{ marginTop: '1rem' }}
            >
              Share your first client
            </button>
          </div>
        ) : (
          <div className="cc-clients-list">
            <p className="section-label" style={{ marginBottom: '1rem' }}>
              {sharedClients.length} shared client{sharedClients.length !== 1 ? 's' : ''}
            </p>
            {(showAllClients ? sharedClients : sharedClients.slice(0, CLIENTS_PREVIEW)).map((client) => {
              const isOwner = client.primaryOwner?._id?.toString() === userId ||
                              client.primaryOwner?.toString() === userId;
              const allContributors = [
                client.primaryOwner,
                ...(client.contributors || []),
              ].filter(Boolean);

              return (
                <div key={client._id} className="cc-client-card">
                  {/* Top row — client info + actions */}
                  <div className="cc-card-top">
                    <Link
                      to={`/clientdashboard/${client._id}`}
                      className="cc-client-link"
                    >
                      <div className="cc-avatar" style={{ background: '#1B2B4B', flexShrink: 0 }}>
                        {getInitials(client.firstName, client.lastName)}
                      </div>
                      <div className="cc-client-info">
                        <p className="cc-client-name">
                          {client.firstName} {client.lastName}
                        </p>
                        <p className="cc-client-meta">{client.email}</p>
                        {client.sharedAt && (
                          <p className="cc-shared-date">
                            Shared {new Date(client.sharedAt).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </p>
                        )}
                      </div>
                    </Link>

                    {/* Contributor avatars */}
                    <div className="cc-contributors">
                      {allContributors.slice(0, 4).map((contributor, i) => {
                        const name = contributor?.firstname
                          ? `${contributor.firstname?.[0]}${contributor.lastname?.[0]}`
                          : '?';
                        return (
                          <div
                            key={i}
                            className="cc-contrib-avatar"
                            style={{
                              background: getAvatarColor(i),
                              marginLeft: i > 0 ? '-8px' : '0',
                              zIndex: allContributors.length - i,
                            }}
                            title={contributor?.firstname
                              ? `${contributor.firstname} ${contributor.lastname}`
                              : 'Member'}
                          >
                            {name.toUpperCase()}
                          </div>
                        );
                      })}
                      {allContributors.length > 4 && (
                        <div className="cc-contrib-avatar cc-contrib-more">
                          +{allContributors.length - 4}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="cc-card-actions">
                      <Link to={`/clientdashboard/${client._id}`} className="cc-view-btn">
                        View →
                      </Link>
                      {isOwner && (
                        <button
                          className="cc-unshare-btn"
                          onClick={() => handleUnshare(client._id, `${client.firstName} ${client.lastName}`)}
                          disabled={unsharing === client._id}
                        >
                          {unsharing === client._id ? '...' : 'Unshare'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Bottom row — AI Persona links */}
                  {(() => {
                    const cid = client._id?.toString() || client._id;
                    const clientPersonas = personas[cid] || [];
                    return clientPersonas.length > 0 ? (
                      <div className="cc-persona-links">
                        {clientPersonas.map((p, i) => (
                          <div key={i} className="cc-persona-link-row">
                            <span className="cc-persona-mark">✦</span>
                            <span className="cc-persona-name">
                              {p.coach?.firstname}'s AI Persona
                              {p.label ? ` · ${p.label}` : ''}
                            </span>
                            <button
                              className="cc-persona-copy"
                              onClick={() => {
                                navigator.clipboard.writeText(p.url);
                                setCopiedToken(p.tokenId);
                                setTimeout(() => setCopiedToken(null), 2000);
                              }}
                            >
                              {copiedToken === p.tokenId ? '✓ Copied' : 'Copy link'}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>
              );
            })}

            {sharedClients.length > CLIENTS_PREVIEW && (
              <button
                className="events-toggle-btn"
                onClick={() => setShowAllClients(!showAllClients)}
              >
                {showAllClients
                  ? 'Show less ▴'
                  : `+ ${sharedClients.length - CLIENTS_PREVIEW} more clients ▾`}
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default CircleClients;
